import Box from '@mui/material/Box'
import Alert from '@mui/material/Alert'
import CircularProgress from '@mui/material/CircularProgress'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import { AiInferencePurpose, type AiUsageSummary } from 'common-billing-server/types'
import { AI_PROVIDER_LABELS } from 'common-billing-server/ai'
import { formatError } from '../../format-error'

const usdFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
})

const formatCost = (microUsd: number) => usdFormatter.format(microUsd / 1_000_000)
const formatTokens = (tokens: number) => tokens.toLocaleString()
const purposeLabels: Record<AiInferencePurpose, string> = {
    [AiInferencePurpose.main]: 'Assistant replies',
    [AiInferencePurpose.title]: 'Conversation titles',
    [AiInferencePurpose.scopeClassifier]: 'Question screening',
    [AiInferencePurpose.expert]: 'Product expert',
    [AiInferencePurpose.expertReviewer]: 'Expert safety review',
    [AiInferencePurpose.actionRiskClassifier]: 'Change risk checks',
}

export const AiUsage = ({ usage, isLoading, error }: { usage?: AiUsageSummary; isLoading: boolean; error?: Error }) => (
    <Paper sx={{ mt: 2, p: 2 }} data-testid="ai-assistant-usage">
        <Typography variant="h6" gutterBottom>
            AI assistant usage
        </Typography>
        {isLoading && !usage ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={24} aria-label="Loading AI assistant usage" />
            </Box>
        ) : error ? (
            <Alert severity="warning">
                {formatError(error, 'AI assistant usage could not be loaded for this billing period')}
            </Alert>
        ) : !usage || usage.requests === 0 ? (
            <Typography>No AI assistant usage found for this billing period</Typography>
        ) : (
            <>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid size={{ xs: 6, md: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            Requests
                        </Typography>
                        <Typography>{formatTokens(usage.requests)}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, md: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            Input tokens
                        </Typography>
                        <Typography>{formatTokens(usage.inputTokens)}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, md: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            Output tokens
                        </Typography>
                        <Typography>{formatTokens(usage.outputTokens)}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, md: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            Cache write tokens
                        </Typography>
                        <Typography>{formatTokens(usage.cacheWriteTokens)}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, md: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            Cache read tokens
                        </Typography>
                        <Typography>{formatTokens(usage.cacheReadTokens)}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, md: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            Calculated cost
                        </Typography>
                        <Typography>{formatCost(usage.costMicroUsd)}</Typography>
                    </Grid>
                </Grid>
                <Box sx={{ overflowX: 'auto', mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                        Usage by purpose
                    </Typography>
                    <Table size="small" data-testid="ai-assistant-usage-by-purpose">
                        <TableHead>
                            <TableRow>
                                <TableCell>Purpose</TableCell>
                                <TableCell align="right">Requests</TableCell>
                                <TableCell align="right">Input</TableCell>
                                <TableCell align="right">Output</TableCell>
                                <TableCell align="right">Cost</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {usage.purposes.map((purpose) => (
                                <TableRow key={purpose.purpose}>
                                    <TableCell>{purposeLabels[purpose.purpose]}</TableCell>
                                    <TableCell align="right">{formatTokens(purpose.requests)}</TableCell>
                                    <TableCell align="right">{formatTokens(purpose.inputTokens)}</TableCell>
                                    <TableCell align="right">{formatTokens(purpose.outputTokens)}</TableCell>
                                    <TableCell align="right">{formatCost(purpose.costMicroUsd)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>
                <Box sx={{ overflowX: 'auto', mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                        Usage by provider
                    </Typography>
                    <Table size="small" data-testid="ai-assistant-usage-by-provider">
                        <TableHead>
                            <TableRow>
                                <TableCell>Provider</TableCell>
                                <TableCell align="right">Requests</TableCell>
                                <TableCell align="right">Input</TableCell>
                                <TableCell align="right">Output</TableCell>
                                <TableCell align="right">Cost</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {usage.providers.map((provider) => (
                                <TableRow key={provider.provider}>
                                    <TableCell>{AI_PROVIDER_LABELS[provider.provider]}</TableCell>
                                    <TableCell align="right">{formatTokens(provider.requests)}</TableCell>
                                    <TableCell align="right">{formatTokens(provider.inputTokens)}</TableCell>
                                    <TableCell align="right">{formatTokens(provider.outputTokens)}</TableCell>
                                    <TableCell align="right">{formatCost(provider.costMicroUsd)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>
                <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small" data-testid="ai-assistant-usage-by-user">
                        <TableHead>
                            <TableRow>
                                <TableCell>Customer</TableCell>
                                <TableCell>Context</TableCell>
                                <TableCell>User</TableCell>
                                <TableCell align="right">Requests</TableCell>
                                <TableCell align="right">Input</TableCell>
                                <TableCell align="right">Output</TableCell>
                                <TableCell align="right">Cache write</TableCell>
                                <TableCell align="right">Cache read</TableCell>
                                <TableCell align="right">Cost</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {usage.users.map((user) => (
                                <TableRow
                                    key={
                                        user.scope === 'console'
                                            ? `${user.customerId}:console:${user.consoleUserId}:${user.consoleUsername}`
                                            : `${user.customerId}:${user.installationId}:${user.edgeUserId}:${user.edgeUsername}`
                                    }
                                    hover
                                >
                                    <TableCell>{user.customerName}</TableCell>
                                    <TableCell>
                                        {user.scope === 'console' ? 'Nimbra Console' : user.installationHost}
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {user.scope === 'console' ? user.consoleUsername : user.edgeUsername}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {user.scope === 'console' ? user.consoleUserId : user.edgeUserId}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">{formatTokens(user.requests)}</TableCell>
                                    <TableCell align="right">{formatTokens(user.inputTokens)}</TableCell>
                                    <TableCell align="right">{formatTokens(user.outputTokens)}</TableCell>
                                    <TableCell align="right">{formatTokens(user.cacheWriteTokens)}</TableCell>
                                    <TableCell align="right">{formatTokens(user.cacheReadTokens)}</TableCell>
                                    <TableCell align="right">{formatCost(user.costMicroUsd)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    {usage.userCount > usage.users.length ? (
                        <Typography variant="caption" color="text.secondary">
                            Showing the {usage.users.length} highest-cost users of {formatTokens(usage.userCount)}.
                        </Typography>
                    ) : null}
                </Box>
            </>
        )}
    </Paper>
)
